import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Animates a number from 0 to `end` when triggered (typically by scroll visibility).
 *
 * @param {number}  end       - Target value
 * @param {boolean} trigger   - When true, animation starts (one-shot)
 * @param {Object}  options
 * @param {number}  options.duration  - Animation duration in ms (default 2000)
 * @param {number}  options.decimals  - Decimal places (default 0)
 * @returns {string} The formatted current value
 */
export function useCountUp(end, trigger, options = {}) {
  const { duration = 2000, decimals = 0 } = options;
  const [value, setValue] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!trigger || hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for a satisfying deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(parseFloat((end * eased).toFixed(decimals)));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setValue(end);
      }
    };

    requestAnimationFrame(animate);
  }, [trigger, end, duration, decimals]);

  return decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
}

/**
 * React component that renders an animated count-up number.
 *
 * Props:
 *   end       {number}  - Target value (e.g. 99.9, 300, 2000)
 *   trigger   {boolean} - Start animation when true
 *   prefix    {string}  - e.g. '<', '>' (default '')
 *   suffix    {string}  - e.g. '%', 'x', '+', 'ms' (default '')
 *   decimals  {number}  - Decimal places (default 0)
 *   duration  {number}  - Animation duration ms (default 2000)
 *   separator {boolean} - Add thousands separator (default false)
 */
export function CountUp({
  end,
  trigger,
  prefix = '',
  suffix = '',
  decimals = 0,
  duration = 2000,
  separator = false,
}) {
  const display = useCountUp(end, trigger, { duration, decimals });
  const formatted = separator
    ? Number(display).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : display;

  return (
    <>
      {prefix}
      {formatted}
      {suffix}
    </>
  );
}
